import React, { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import { getThemeColors } from './styles';
import {
  Header,
  TitleSection,
  Avatar,
  TitleText,
  Title,
  Subtitle,
  Controls,
  ControlButton,
} from './styles';

// ── Overflow dropdown ────────────────────────────────────────────────────────

const OverflowWrapper = styled.div`
  position: relative;
`;

const OverflowMenu = styled.div<{ isDarkMode: boolean }>`
  ${({ isDarkMode }) => {
    const c = getThemeColors(isDarkMode);
    return css`
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      z-index: 2000;
      min-width: 210px;
      background: ${c.background};
      border: 1px solid ${c.borderColor};
      border-radius: 10px;
      box-shadow: ${c.shadowLg};
      padding: 4px;
    `;
  }}
`;

const MenuItem = styled.button<{ isDarkMode: boolean }>`
  ${({ isDarkMode }) => {
    const c = getThemeColors(isDarkMode);
    return css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 9px 12px;
      background: transparent;
      border: none;
      border-radius: 7px;
      color: ${c.textPrimary};
      font-size: 13px;
      font-family: inherit;
      cursor: pointer;
      transition: background 0.12s;
      text-align: left;

      &:hover {
        background: ${c.surfaceHover};
      }
    `;
  }}
`;

const MenuItemLeft = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  color: inherit;
`;

const MenuDivider = styled.div<{ isDarkMode: boolean }>`
  height: 1px;
  background: ${({ isDarkMode }) => getThemeColors(isDarkMode).borderColor};
  margin: 3px 8px;
`;

const TogglePill = styled.span<{ isActive: boolean }>`
  display: inline-flex;
  width: 30px;
  height: 17px;
  border-radius: 999px;
  background: ${({ isActive }) => (isActive ? '#f34452' : '#666')};
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ isActive }) => (isActive ? '15px' : '2px')};
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
  }
`;

// ── Component ────────────────────────────────────────────────────────────────

interface WidgetHeaderProps {
  isDarkMode: boolean;
  isMaximized: boolean;
  isSidebarOpen: boolean;
  isAutoPlayEnabled: boolean;
  onToggleSidebar: () => void;
  onToggleMaximize: () => void;
  onToggleDarkMode: () => void;
  onToggleAutoPlay: () => void;
  onNewChat: () => void;
  onClose: () => void;
}

const WidgetHeader: React.FC<WidgetHeaderProps> = ({
  isDarkMode,
  isMaximized,
  isSidebarOpen,
  isAutoPlayEnabled,
  onToggleSidebar,
  onToggleMaximize,
  onToggleDarkMode,
  onToggleAutoPlay,
  onNewChat,
  onClose,
}) => {
  const { t } = useLanguage();
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOverflowOpen) return;
    const handler = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setIsOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOverflowOpen]);

  const handleMenuAction = (action: () => void) => {
    action();
    // Don't close — let user toggle multiple settings in one session
  };

  return (
    <Header isDarkMode={isDarkMode}>
      <TitleSection>
        <Avatar>
          <img loading="lazy" decoding="async"
            src="/images/bg/me.webp"
            alt={t('widget.title')}
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
          />
        </Avatar>
        <TitleText>
          <Title isDarkMode={isDarkMode}>{t('widget.title')}</Title>
          <Subtitle isDarkMode={isDarkMode}>{t('widget.subtitle')}</Subtitle>
        </TitleText>
      </TitleSection>

      <Controls>
        {/* ⋮ Overflow */}
        <OverflowWrapper ref={overflowRef}>
          <ControlButton
            onClick={() => setIsOverflowOpen(p => !p)}
            isDarkMode={isDarkMode}
            isActive={isOverflowOpen}
            aria-label="More options"
            title="More options"
            aria-expanded={isOverflowOpen}
            aria-haspopup="true"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="5" r="1" />
              <circle cx="12" cy="12" r="1" />
              <circle cx="12" cy="19" r="1" />
            </svg>
          </ControlButton>

          {isOverflowOpen && (
            <OverflowMenu isDarkMode={isDarkMode} role="menu">
              {/* Auto-play toggle */}
              <MenuItem
                isDarkMode={isDarkMode}
                role="menuitemcheckbox"
                aria-checked={isAutoPlayEnabled}
                onClick={() => handleMenuAction(onToggleAutoPlay)}
              >
                <MenuItemLeft>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                  {t('widget.voice.autoPlay')}
                </MenuItemLeft>
                <TogglePill isActive={isAutoPlayEnabled} />
              </MenuItem>

              {/* Sidebar toggle */}
              <MenuItem
                isDarkMode={isDarkMode}
                role="menuitemcheckbox"
                aria-checked={isSidebarOpen}
                onClick={() => handleMenuAction(onToggleSidebar)}
              >
                <MenuItemLeft>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <line x1="9" y1="3" x2="9" y2="21" />
                  </svg>
                  {t('widget.toggleSidebar')}
                </MenuItemLeft>
                <TogglePill isActive={isSidebarOpen} />
              </MenuItem>

              {/* Maximize toggle */}
              <MenuItem
                isDarkMode={isDarkMode}
                role="menuitemcheckbox"
                aria-checked={isMaximized}
                onClick={() => handleMenuAction(onToggleMaximize)}
              >
                <MenuItemLeft>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                  {isMaximized ? 'Restore window' : 'Maximize window'}
                </MenuItemLeft>
                <TogglePill isActive={isMaximized} />
              </MenuItem>

              <MenuDivider isDarkMode={isDarkMode} />

              {/* Dark mode toggle */}
              <MenuItem
                isDarkMode={isDarkMode}
                role="menuitemcheckbox"
                aria-checked={isDarkMode}
                onClick={() => handleMenuAction(onToggleDarkMode)}
              >
                <MenuItemLeft>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                  Dark mode
                </MenuItemLeft>
                <TogglePill isActive={isDarkMode} />
              </MenuItem>
            </OverflowMenu>
          )}
        </OverflowWrapper>

        {/* + New chat */}
        <ControlButton
          onClick={onNewChat}
          isDarkMode={isDarkMode}
          aria-label={t('widget.newChat')}
          title={t('widget.newChat')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </ControlButton>

        {/* ✕ Close */}
        <ControlButton
          onClick={onClose}
          isDarkMode={isDarkMode}
          isClose
          aria-label={t('widget.close')}
          title={t('widget.close')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </ControlButton>
      </Controls>
    </Header>
  );
};

export default WidgetHeader;
